/**
 * Synk-intervaller pr. 3. part ([D-356], 02-10-2026).
 *
 * Charlie: "Jeg vil gerne kunne ændre indstillingerne for hvor ofte der
 * synkroniseres data mellem Cockpit og en 3. part ... Intervallet skal angives
 * i minutter, hvis det skal ske én gang i timen, skal man angive 60."
 *
 * REGISTRET her er den ENE kilde til hvilke synkroniseringer der har et
 * interval, hvilke sider der viser det (tandhjulet + Indstillinger →
 * Sideindstillinger), og hvilke grænser der gælder. Værdien bor ét sted pr.
 * kilde (tabellen sync_schedules, Prisskilte dog i regelsættet), så en kilde
 * der vises på 2 sider (Typeform på Bookinger og Henvendelser) aldrig kan have
 * 2 forskellige intervaller.
 *
 * 1440 minutter = 1 gang i døgnet = kun natkørslen (marketing-kaskaden), som
 * kører uændret for alle kilderne. Et kortere interval lægger kørsler i løbet
 * af dagen OVENI, målt fra kildens seneste kørsel (også nattens og en manuel).
 *
 * Bevidst IKKE med (Charlie kan vælge dem til): kilder der måler pr. dag
 * (GA4, Search Console, annonceplatformene, Cloudflare, Stape), hvor en
 * kørsel midt på dagen kun giver en halv dag i dagens tal, kilder der koster
 * pr. opslag (DataForSEO, Konkurrenter), og Business Central, der har sit eget
 * natjob (03:30 UTC).
 */

import type { PageSettingFieldDef } from './page-settings.js';

/** 1 gang i døgnet = kun natkørslen. */
export const SYNC_NIGHTLY_ONLY_MINUTES = 1440;

/**
 * Sync-doktorens grænse: tager en kørsel typisk mere end så stor en andel af
 * intervallet, griber doktoren ind. 12 min målt mod 5 min ligger langt over;
 * efter et løft til 15 min ligger den på 80 % og er dermed i orden.
 */
export const SYNC_DOCTOR_MAX_BUSY_PCT = 80;
/** Så mange af de seneste kørsler i løbet af dagen indgår i medianen. */
export const SYNC_DOCTOR_RUNS_MEASURED = 5;
/** Mindst så mange kørsler skal være målt, før doktoren griber ind. */
export const SYNC_DOCTOR_MIN_RUNS = 2;

/**
 * Inkrementel synkronisering (kun det der er ændret siden sidst):
 *  - 'native': synkroniseringen er ALTID inkrementel (vandmærke eller delta).
 *  - 'option': kan slås til pr. kilde; kørslerne i løbet af dagen henter så
 *    kun et udsnit, mens natkørslen fortsat henter alt.
 *  - 'none': kilden kan ikke hentes inkrementelt (se `incrementalNote`).
 */
export type SyncIncrementalSupport = 'native' | 'option' | 'none';

export type SyncSourceId =
  | 'loyaltykey'
  | 'calendly'
  | 'typeform'
  | 'fluentforms'
  | 'qrtiger'
  | 'breece'
  | 'ms365_calendar'
  | 'klaviyo_campaigns';

export interface SyncSourceDef {
  id: SyncSourceId;
  /** Kildens navn i UI og mails. */
  label: string;
  /** Hvad der synkroniseres (kort). */
  what: string;
  /** Retning: hentes data ind i Cockpit, eller sender Cockpit ud? */
  direction: 'ind' | 'ud';
  /** Siderne hvis tandhjul viser intervallet; den første er kildens "hjem". */
  pages: readonly string[];
  minMinutes: number;
  maxMinutes: number;
  defaultMinutes: number;
  incremental: SyncIncrementalSupport;
  /** Kun for 'option': standardværdien for fluebenet. */
  defaultIncremental?: boolean;
  /** Forklaring på hvordan (eller hvorfor ikke) kilden kan hentes inkrementelt. */
  incrementalNote: string;
  /** integration_accounts.provider, hvis den KUN bruges af denne kilde (seneste kørsel). */
  statusProvider?: string;
}

export const SYNC_SOURCES: readonly SyncSourceDef[] = [
  {
    id: 'loyaltykey',
    label: 'Loyalty Key',
    what: 'cashback-transaktioner og kontrollen af butikshandler',
    direction: 'ind',
    pages: ['/marketing/affiliates/loyalty-key'],
    minMinutes: 5,
    maxMinutes: SYNC_NIGHTLY_ONLY_MINUTES,
    defaultMinutes: SYNC_NIGHTLY_ONLY_MINUTES,
    incremental: 'none',
    incrementalNote:
      'Loyalty Keys API ser bort fra alle filtre (målt 01-10-2026), så hele transaktionslisten hentes hver gang.',
    statusProvider: 'loyaltykey',
  },
  {
    id: 'calendly',
    label: 'Calendly',
    what: 'bookinger med deltagere, aflysninger og udeblivelser',
    direction: 'ind',
    pages: ['/inbound/bookings', '/inbound/leads'],
    minMinutes: 5,
    maxMinutes: SYNC_NIGHTLY_ONLY_MINUTES,
    defaultMinutes: SYNC_NIGHTLY_ONLY_MINUTES,
    incremental: 'option',
    defaultIncremental: false,
    incrementalNote:
      'Inkrementel: kørslerne i løbet af dagen henter kun møder fra 14 dage tilbage og frem (nye bookinger, aflysninger, flytninger og udeblivelser); natkørslen henter alle.',
    statusProvider: 'calendly',
  },
  {
    // [D-416]: mødeindkaldelser i alle @badogfliser.dk-postkassers kalendere
    // (info@ m.fl.), så Klaviyo-lead-agenten ser manuelt oprettede møder.
    id: 'ms365_calendar',
    label: 'Outlook-kalendere (MS365)',
    what: 'mødeindkaldelser med eksterne deltagere i alle postkassers kalendere, og mail-kontakter fra eksterne afsendere (kun adresse og dag)',
    direction: 'ind',
    pages: ['/inbound/leads', '/customers/journey'],
    minMinutes: 15,
    maxMinutes: SYNC_NIGHTLY_ONLY_MINUTES,
    defaultMinutes: 60,
    incremental: 'none',
    incrementalNote:
      'Hele vinduet (fra lead-agentens startdato til 1 år frem) hentes hver gang, så aflyste og slettede møder også samles op.',
    statusProvider: 'microsoft_graph_calendar',
  },
  {
    id: 'typeform',
    label: 'Typeform',
    what: 'formular-svar (bookinger og henvendelser)',
    direction: 'ind',
    pages: ['/inbound/bookings', '/inbound/inquiries', '/inbound/leads'],
    minMinutes: 5,
    maxMinutes: SYNC_NIGHTLY_ONLY_MINUTES,
    defaultMinutes: SYNC_NIGHTLY_ONLY_MINUTES,
    incremental: 'native',
    incrementalNote: 'Altid inkrementel: kun svar indsendt siden seneste kendte svar pr. formular hentes.',
    statusProvider: 'typeform',
  },
  {
    id: 'fluentforms',
    label: 'FluentForms',
    what: 'kontakt- og prisformularer fra webshoppen',
    direction: 'ind',
    pages: ['/inbound/inquiries', '/inbound/leads'],
    minMinutes: 15,
    maxMinutes: SYNC_NIGHTLY_ONLY_MINUTES,
    defaultMinutes: SYNC_NIGHTLY_ONLY_MINUTES,
    incremental: 'none',
    incrementalNote:
      'Hele spejlet hentes hver gang (ca. 660 indsendelser), fordi status kan ændre sig på gamle indsendelser.',
  },
  {
    id: 'qrtiger',
    label: 'QR TIGER',
    what: 'QR-koder og scanninger pr. dag',
    direction: 'ind',
    pages: ['/marketing/qr-codes'],
    minMinutes: 15,
    maxMinutes: SYNC_NIGHTLY_ONLY_MINUTES,
    defaultMinutes: SYNC_NIGHTLY_ONLY_MINUTES,
    incremental: 'none',
    incrementalNote: 'Koderne og deres scanninger hentes samlet hver gang; QR TIGER tæller scanninger pr. dag.',
    statusProvider: 'qrtiger',
  },
  {
    // [D-546]: kampagnemails med status og rapport-tal hver time; profilerne
    // (hele modtagerlisten) og flowmailenes ugeserier hentes kun om natten.
    id: 'klaviyo_campaigns',
    label: 'Klaviyo (kampagnemails)',
    what: 'kampagnemails med status, afsendelsestidspunkt og rapport-tal (modtagere, åbninger, klik og købere)',
    direction: 'ind',
    pages: ['/marketing/email'],
    // Klaviyos rapport-API tillader 225 kald pr. døgn; 1 kørsel bruger 1 til 2.
    minMinutes: 30,
    maxMinutes: SYNC_NIGHTLY_ONLY_MINUTES,
    defaultMinutes: 60,
    incremental: 'none',
    incrementalNote:
      'Hele kampagnelisten og rapporten for de seneste 12 måneder hentes hver gang. Profilerne (hele modtagerlisten) og flowmailenes ugeserier hentes kun af natkørslen.',
  },
  {
    id: 'breece',
    label: 'Breece Cloud (prisskilte)',
    what: 'ændrede varer fra Business Central til de digitale prisskilte',
    direction: 'ud',
    pages: ['/stores/price-tags/signs'],
    // Samme grænser som Regelsæt (routes/price-tags.ts), hvor værdien også kan rettes.
    minMinutes: 1,
    maxMinutes: 60,
    defaultMinutes: 5,
    incremental: 'native',
    incrementalNote:
      'Altid inkrementel: hver kørsel sender kun varer hvis skilteværdier har flyttet sig (delta); den fulde indlæsning startes med Kør nu.',
  },
];

export function getSyncSource(id: string): SyncSourceDef | undefined {
  return SYNC_SOURCES.find((s) => s.id === id);
}

/** Kilderne en side viser i sit tandhjul (registrets rækkefølge). */
export function syncSourcesForPage(routePath: string): SyncSourceDef[] {
  return SYNC_SOURCES.filter((s) => s.pages.includes(routePath));
}

/** Sanér et interval mod kildens grænser (hele minutter). */
export function clampSyncMinutes(src: SyncSourceDef, raw: number): number {
  if (!Number.isFinite(raw)) return src.defaultMinutes;
  return Math.min(src.maxMinutes, Math.max(src.minMinutes, Math.round(raw)));
}

/**
 * "Pæne" intervaller Sync-doktoren hæver til: 12 min målt mod 5 min giver 15,
 * ikke 15,3. Rækken går til 1 døgn.
 */
export const SYNC_INTERVAL_STEPS: readonly number[] = [
  1, 2, 3, 5, 10, 15, 20, 30, 45, 60, 90, 120, 180, 240, 360, 480, 720, 1440,
];

/** Mindste trin i rækken der er mindst `minutes`; null hvis over et døgn. */
export function niceSyncIntervalAtLeast(minutes: number): number | null {
  for (const step of SYNC_INTERVAL_STEPS) if (step >= minutes) return step;
  return null;
}

/** Feltnøglerne i sideindstillingerne (én pr. kilde, delt af alle sider der viser den). */
export function syncIntervalKey(id: SyncSourceId): string {
  return `sync_${id}_minutes`;
}
export function syncIncrementalKey(id: SyncSourceId): string {
  return `sync_${id}_incremental`;
}

/** Interval i ord: "hvert 15. minut", "1 gang i timen", "1 gang i døgnet (kun natkørslen)". */
export function describeSyncInterval(minutes: number): string {
  if (minutes >= SYNC_NIGHTLY_ONLY_MINUTES) return '1 gang i døgnet (kun natkørslen)';
  if (minutes === 60) return '1 gang i timen';
  if (minutes === 1) return 'hvert minut';
  if (minutes % 60 === 0) return `hver ${minutes / 60}. time`;
  return `hvert ${minutes}. minut`;
}

/**
 * Sideindstillings-felterne for en sides synk-kilder: et interval pr. kilde
 * og, hvor kilden kan, fluebenet for inkrementel synkronisering. Typerne
 * 'syncInterval' og 'syncIncremental' gemmes IKKE i page_settings, men i
 * kildens eget lager (se server/src/lib/sync-schedules.ts).
 */
export function syncSettingsFields(routePath: string): PageSettingFieldDef[] {
  const out: PageSettingFieldDef[] = [];
  for (const s of syncSourcesForPage(routePath)) {
    out.push({
      key: syncIntervalKey(s.id),
      label: `Synkronisering med ${s.label}`,
      help:
        s.direction === 'ud'
          ? `Hvor ofte Cockpit sender ${s.what}. Angives i minutter (${s.minMinutes} til ${s.maxMinutes}).`
          : `Hvor ofte ${s.what} hentes. Angives i minutter: 60 = 1 gang i timen, ${SYNC_NIGHTLY_ONLY_MINUTES} = kun natkørslen.`,
      type: 'syncInterval',
      syncSource: s.id,
      min: s.minMinutes,
      max: s.maxMinutes,
      step: 1,
      unit: 'min',
    });
    if (s.incremental === 'option') {
      out.push({
        key: syncIncrementalKey(s.id),
        label: `Inkrementel synkronisering med ${s.label}`,
        help: s.incrementalNote,
        type: 'syncIncremental',
        syncSource: s.id,
      });
    }
  }
  return out;
}

/** Defaults til de samme felter (registrets standardværdier). */
export function syncSettingsDefaults(routePath: string): Record<string, number | boolean> {
  const out: Record<string, number | boolean> = {};
  for (const s of syncSourcesForPage(routePath)) {
    out[syncIntervalKey(s.id)] = s.defaultMinutes;
    if (s.incremental === 'option') out[syncIncrementalKey(s.id)] = s.defaultIncremental ?? false;
  }
  return out;
}

/** Status pr. kilde til tandhjulet og oversigten (GET /api/page-settings/sync). */
export interface SyncSourceStatusDto {
  id: SyncSourceId;
  label: string;
  pages: string[];
  intervalMinutes: number;
  incremental: boolean;
  incrementalSupport: SyncIncrementalSupport;
  /** Er intervallet slået til? (Prisskilte: automatikken på Regelsæt.) */
  active: boolean;
  /** Hvorfor kilden står stille, hvis den gør (fx automatikken slået fra). */
  inactiveReason: string | null;
  updatedAt: number | null;
  updatedBy: string | null;
  lastRunAt: number | null;
  lastRunOk: boolean | null;
  lastRunDurationMs: number | null;
  /** Median af de seneste kørsler i løbet af dagen (de samme som Sync-doktoren måler). */
  typicalDurationMs: number | null;
  runsMeasured: number;
  canEdit: boolean;
}

/** Ét fund fra Sync-doktoren (ændring eller anbefaling). */
export interface SyncDoctorFindingDto {
  id: number;
  sourceId: string;
  sourceLabel: string;
  kind: 'interval_raised' | 'incremental_enabled' | 'recommendation';
  intervalBefore: number | null;
  intervalAfter: number | null;
  typicalDurationMs: number | null;
  runsMeasured: number | null;
  message: string;
  createdAt: number;
  mailedAt: number | null;
  mailError: string | null;
}
